import {
    Component,
    OnDestroy,
    OnInit,
    inject,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormsModule, ReactiveFormsModule, UntypedFormGroup } from "@angular/forms";
import { ActivatedRoute, Router } from "@angular/router";
import { ActionButtonComponent } from "@components/action-button/action-button.component";
import { BlockComponent } from "@components/container-block/block.component";
import { PageComponent } from "@components/page/page.component";
import { SaveButtonComponent } from "@components/save-button/save-button.component";
import { TabComponent } from "@components/tabs/tab/tab.component";
import { TabsComponent } from "@components/tabs/tabs.component";
import {
    NgbDropdown,
    NgbDropdownItem,
    NgbDropdownMenu,
    NgbDropdownToggle,
    NgbModal
} from "@ng-bootstrap/ng-bootstrap";
import {
    Subject,
    combineLatest,
    distinctUntilChanged,
    firstValueFrom,
    map,
    switchMap,
    takeUntil,
    tap,
    timer
} from "rxjs";
import { BusyService } from "~app/common/components/busy/busy.service";
import { DataChangeLogComponent } from "~app/common/components/data-change-log/data-change-log.component";
import { ModalSettings } from "~app/common/components/modal/modal-models";
import { RaskModalService } from "~app/common/components/modal/rask-modal.service";
import { ToastService } from "~app/common/components/toast/toast.service";
import { ConfirmDialogResponse } from "~app/common/models/constants";
import { ProduktDTO } from "~app/common/models/produkt/ProduktDTO";
import { ProduktDeleteRequestDTO } from "~app/common/models/produkt/ProduktDeleteRequestDTO";
import { ProduktGrunddataResponseDTO } from "~app/common/models/produkt/ProduktGrunddataResponseDTO";
import { ProduktspecifikationDTO } from "~app/common/models/produkt/ProduktspecifikationDTO";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { DirtyFormService } from "~app/common/services/dirty-form.service";
import { RaskTitleService } from "~app/common/services/title.service";
import { CommonTexts } from "~app/common/texts/common.texts";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { IValidationContext } from "~app/common/validation/IValidationContext";
import { IValidationScope } from "~app/common/validation/IValidationScope";
import { ProduktService } from "../produkt.service";
import { ProduktDetailHeaderComponent } from "./produkt-detail-header/produkt-detail-header.component";
import { ProduktDetailProduktspecifikationComponent } from "./produkt-detail-produktspecifikation/produkt-detail-produktspecifikation.component";
import { ProduktDetailTexts } from "./produkt-detail.texts";
import { FormMode, FormModeName, HandelssortimentsgruppKategori, TabName } from "./produkt-details.models";
import { ProduktDetailBasComponent } from "./tabs/produkt-detail-bas/produkt-detail-bas.component";
import { ProduktDetailFordelningsmatrisComponent } from "./tabs/produkt-detail-fordelningsmatris/produkt-detail-fordelningsmatris.component";
import { ProduktDetailProduktinstruktionerComponent } from "./tabs/produkt-detail-produktinstruktioner/produkt-detail-produktinstruktioner.component";
import { ProduktDetailUppfoljningComponent } from "./tabs/produkt-detail-uppfoljning/produkt-detail-uppfoljning.component";

@Component({
    selector: "rask-produkt-detail",
    templateUrl: "./produkt-detail.component.html",
    styleUrls: ["./produkt-detail.component.scss"],
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        PageComponent,
        ActionButtonComponent,
        NgbDropdown,
        NgbDropdownToggle,
        NgbDropdownMenu,
        NgbDropdownItem,
        ProduktDetailHeaderComponent,
        ProduktDetailProduktspecifikationComponent,
        BlockComponent,
        TabsComponent,
        TabComponent,
        ProduktDetailBasComponent,
        ProduktDetailFordelningsmatrisComponent,
        ProduktDetailUppfoljningComponent,
        ProduktDetailProduktinstruktionerComponent,
        SaveButtonComponent
    ]
})
export class ProduktDetailComponent implements OnInit, OnDestroy {
    readonly modalService = inject(NgbModal);

    texts = ProduktDetailTexts.texts;
    commonTexts = CommonTexts.texts;
    fordelningsmatrisTabOpen: boolean;
    form: UntypedFormGroup;
    formMode = FormMode.NotSet;
    initialized = false;
    produkt: ProduktDTO;
    produktBasdata: ProduktGrunddataResponseDTO;
    produktspecifikation: ProduktspecifikationDTO;
    tabNames = TabName;
    validation: IValidationScope;
    validationContext: IValidationContext;

    private readonly activatedRoute = inject(ActivatedRoute);
    private readonly authenticationService = inject(AuthenticationService);
    private readonly busyService = inject(BusyService);
    private readonly destroy$ = new Subject<void>();
    private readonly dirtyFormService = inject(DirtyFormService);
    private readonly produktService = inject(ProduktService);
    private readonly raskModalService = inject(RaskModalService);
    private readonly raskTitleService = inject(RaskTitleService);
    private readonly router = inject(Router);
    private readonly toastService = inject(ToastService);

    private editableRole: boolean;

    get canCopy(): boolean {
        return this.formMode === FormMode.Edit && this.editableRole;
    }

    get canDelete(): boolean {
        return this.formMode === FormMode.Edit && this.editableRole;
    }

    get canSave(): boolean {
        return this.form.dirty && this.form.valid && this.editableRole;
    }

    get canShowLog(): boolean {
        return this.formMode === FormMode.Edit;
    }

    get displayFordelningsmatristab(): boolean {
        return this.produkt &&
            this.produkt.handelssortimentsgrupp &&
            !(this.produkt.handelssortimentsgrupp.kategori === HandelssortimentsgruppKategori.energi ||
                this.produkt.handelssortimentsgrupp.kategori === HandelssortimentsgruppKategori.ovrigt);
    }

    get displayProduktspecifikationTabs(): boolean {
        return !ObjectUtils.isNullOrUndefined(this.produkt) && !ObjectUtils.isNullOrUndefined(this.produkt.produktspecifikation);
    }

    get title(): string {
        return this.formMode === FormMode.New
            ? this.texts.pageTitleNewProdukt
            : this.texts.pageTitleProdukt;
    }

    copyProdukt(includeSpecifikation: boolean): void {
        const produktspecifikationId = includeSpecifikation
            ? this.produktspecifikation.id
            : null;
        const worker = this.busyService.addWork(
            this.produktService.copyProdukt(
                this.produkt.id,
                produktspecifikationId
            )
        );

        this.form.reset();
        this.initialized = false;

        worker.subscribe((result) => {
            this.produkt = result.produkt;
            this.produktspecifikation = result.produktspecifikation;

            this.formMode = FormMode.Copy;

            this.buildForm(true);
        });
    }

    copyProduktspecifikation(): void {
        const worker = this.busyService.addWork(
            this.produktService.copyProduktspecifikation(
                this.produkt.produktspecifikation
            )
        );

        this.initialized = false;

        worker.subscribe((result) => {
            this.produktspecifikation = result.produktspecifikation;
            this.produkt.produktspecifikation = this.produktspecifikation.id;

            this.formMode = FormMode.Copy;

            this.buildForm(true);
        });
    }

    async delete(): Promise<void> {
        const result = await firstValueFrom(this.raskModalService
            .confirm(new ModalSettings(this.texts.deletePromtTitle, this.texts.deletePromptText)));

        if (result === ConfirmDialogResponse.save) {
            const request: ProduktDeleteRequestDTO = {
                id: this.produkt.id,
                rowVersion: this.produkt.rowVersion
            };

            const worker = this.busyService.addWork(
                this.produktService.deleteProduktById(request)
            );

            worker.subscribe(() => {
                this.toastService.add(this.texts.deleteProduktConfirmed);
                this.router.navigate(["/produkt"]);
            });
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    ngOnInit(): void {
        const produktId$ = this.activatedRoute.params.pipe(
            map(params => params.id),
            tap(id => this.handleParams(id)),
            distinctUntilChanged()
        );

        const produkt$ = produktId$.pipe(
            switchMap(id => {
                const produktId = +id;
                if (isNaN(produktId)) {
                    return this.produktService.getNewProdukt();
                }
                return this.produktService.getProduktById(produktId);
            })
        );

        this.editableRole = this.authenticationService.canEdit();

        combineLatest([
            this.produktService.getProduktgrunddata(),
            produkt$
        ]
        ).pipe(
            takeUntil(this.destroy$)
        ).subscribe((result) => {
            const [
                basdata,
                produkt
            ] = result;
            this.produktBasdata = basdata;
            this.produkt = produkt.produkt;
            this.produktspecifikation = produkt.produktspecifikation;

            this.buildForm();

            this.dirtyFormService.onDirty(
                this.form,
                this.save.bind(this),
                d => {
                    // Krävs
                },
                ["produkt-detail"]);
        });
    }

    onProduktspecifikationChanged = (produktspecifikation: ProduktspecifikationDTO): void => {
        this.produktspecifikation = produktspecifikation;
        this.produkt.produktspecifikation = this.produktspecifikation.id;

        this.initialized = false;

        this.buildForm();
    };

    openFordelningsmatrisTab() {
        this.fordelningsmatrisTabOpen = true;
    }

    save(): void {
        if (!this.canSave) {
            return;
        }

        const worker = this.busyService.addWork(
            this.produktService.saveProdukt(
                this.produkt,
                this.produktspecifikation
            )
        );

        worker.subscribe((result) => {
            if (result) {
                this.initialized = false;
                this.produkt = result.produkt;
                this.produktspecifikation = result.produktspecifikation;

                this.dirtyFormService.form = null;
                const tab = this.activatedRoute.snapshot.paramMap.get("tab");
                this.router.navigate([
                    "/produkt",
                    this.produkt.id,
                    tab
                ]);
                this.buildForm();
                this.formMode = FormMode.Edit;
                this.toastService.add(this.texts.saveProduktConfirmed);
            }
        });
    }

    showLog(): void {
        const modalRef = this.modalService.open(DataChangeLogComponent, {
            size: "xl",
            centered: false
        });
        modalRef.componentInstance.table = "Produkter";
        modalRef.componentInstance.referenceId = this.produkt.id;
    }

    private buildForm(markAsDirty = false): void {
        this.updateTitle();
        this.form = new UntypedFormGroup({});
        this.form.markAsPristine();
        timer(10).subscribe(() => {
            this.initialized = true;

            // Fix för att formuläret ska hinna bygga innan dirtyFormService får det nya.
            // Detta är för att vi bygger en ny FormGroup vid ändringar och dirtyFormService tittar fortfarande på gamla FormGroupen.
            this.busyService.addWork(timer(1000))
                .subscribe(() => {
                    this.dirtyFormService.form = this.form;
                    if (markAsDirty) {
                        this.form.markAsDirty();
                    }
                });
        });
    }

    private handleParams(id: string): void {
        switch (id) {
            case FormModeName.New:
                this.formMode = FormMode.New;
                break;
            case FormModeName.Copy:
                this.formMode = FormMode.Copy;
                break;
            default:
                this.formMode = FormMode.Edit;
                break;
        }

        // If reloading page when in copy-mode -> redirect to new
        if (this.formMode === FormMode.Copy) {
            this.router.navigate([
                "/produkt",
                FormModeName.New
            ]);
        }
    }

    private updateTitle(): void {
        if (this.formMode === FormMode.New) {
            this.raskTitleService.setTitle("Skapa ny produkt");
        } else {
            this.raskTitleService.setTitle(`${this.produkt?.namn}`);
        }
    }
}
